import { Link } from "react-router-dom";

function LegalShell({ title, updated, children }) {
  return (
    <div className="min-h-full bg-[#F5F6FA]">
      <header className="bg-[#101D51] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <img src="/tradedesk-mark.png" alt="TradeDesk360" className="w-8 h-8 rounded-lg bg-white p-1 object-contain" />
            <span className="font-bold text-lg">TradeDesk360</span>
          </Link>
          <Link to="/login" className="text-sm text-white/80 hover:text-white">Sign in</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-extrabold text-[#101D51]">{title}</h1>
        <p className="text-sm text-[#6B7189] mt-1">Last updated: {updated}</p>
        <div className="prose-legal mt-8 space-y-5 text-[15px] leading-relaxed text-[#374151]">{children}</div>
        <p className="text-xs text-[#9AA0B4] mt-12 border-t border-[#E6E8F0] pt-6">
          TradeDesk360 is a product of Clotus Infotech. This document is a general template and should be
          reviewed by your legal counsel before commercial use. Questions: <a className="text-[#F83A84]" href="mailto:sales1@clotus.tech">sales1@clotus.tech</a>.
        </p>
      </main>
    </div>
  );
}

const H = ({ children }) => <h2 className="text-lg font-bold text-[#101D51] pt-2">{children}</h2>;

export function Terms() {
  return (
    <LegalShell title="Terms of Service" updated="June 2026">
      <p data-testid="terms-body">These Terms govern your access to and use of the TradeDesk360 software-as-a-service platform ("Service") operated by Clotus Infotech ("we", "us"). By creating an account or using the Service you agree to these Terms.</p>
      <H>1. Accounts &amp; eligibility</H>
      <p>You must provide accurate information and are responsible for activity under your account and for keeping your login (mobile number and one-time codes) secure. You must be authorised to act for the business you register.</p>
      <H>2. Subscriptions &amp; trials</H>
      <p>The Service is offered on a free trial and paid subscription basis. Fees, trial length and limits are shown at sign-up or in your plan. Paid fees are billed in advance and are non-refundable except where required by law.</p>
      <H>3. Acceptable use</H>
      <p>You agree not to misuse the Service, send unlawful or unsolicited messages via connected channels (including WhatsApp), infringe others' rights, or attempt to breach security or access data that is not yours.</p>
      <H>4. Customer data</H>
      <p>You retain ownership of the data you upload. You grant us a limited licence to process it solely to provide the Service. Your use of connected third-party services (e.g. WhatsApp Business, payment or CRM providers) is also subject to their terms.</p>
      <H>5. Availability &amp; changes</H>
      <p>We aim for high availability but the Service is provided "as is" without warranty of uninterrupted operation. We may update features and these Terms; material changes will be notified.</p>
      <H>6. Liability</H>
      <p>To the maximum extent permitted by law, our aggregate liability is limited to the fees you paid in the 3 months preceding a claim. We are not liable for indirect or consequential loss.</p>
      <H>7. Termination</H>
      <p>You may cancel anytime. We may suspend or terminate accounts that breach these Terms. On termination you may export your data for a reasonable period.</p>
      <H>8. Governing law</H>
      <p>These Terms are governed by the laws of India, with courts at the registered office of Clotus Infotech having jurisdiction.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" updated="June 2026">
      <p data-testid="privacy-body">This Policy explains how Clotus Infotech collects, uses and protects personal data when you use TradeDesk360, in line with India's Digital Personal Data Protection Act, 2023 and applicable data-protection principles.</p>
      <H>1. Data we collect</H>
      <p>Account details (name, business name, mobile number, email), the business records you create (customers, enquiries, quotes, orders, invoices, inventory), WhatsApp messages you exchange through the Service, and technical logs needed to run and secure the platform.</p>
      <H>2. How we use it</H>
      <p>To provide and improve the Service, authenticate logins via one-time codes, generate documents, send transactional notifications, provide support, and meet legal obligations. We do not sell your personal data.</p>
      <H>3. Sharing &amp; processors</H>
      <p>We share data only with sub-processors that help us operate the Service (e.g. cloud hosting, messaging via Meta WhatsApp Business, email delivery, and — where you enable it — your CRM). Each processes data under contract and only on our instructions.</p>
      <H>4. Storage &amp; security</H>
      <p>Data is stored on secured infrastructure with access controls, encryption in transit, and tenant isolation so one business cannot see another's data. We retain data for as long as your account is active or as required by law.</p>
      <H>5. Your rights</H>
      <p>Subject to law, you may access, correct, or request deletion of your personal data, and withdraw consent. Contact us to exercise these rights.</p>
      <H>6. Contact</H>
      <p>Data requests and questions: <a className="text-[#F83A84]" href="mailto:sales1@clotus.tech">sales1@clotus.tech</a>.</p>
    </LegalShell>
  );
}
